import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { SD, RG } from '../../../../../../utils/constants';
import unexpectedErrorMessage from '../../../../../../utils/unexpectedErrorMessage';
import StripePaymentButtonElement from '../../../../../01_atoms/Payments/StripePaymentButtonElement';
import {
  donationFormLock,
  donationFormPaymentSuccess,
  donationFormStripeButtonAvailability,
  donationFormSubmitError,
  donationFormUnLock,
} from '../../../../../../store/Slices/cnet/donationFormSlice';

/**
 * DonationStripeButton component provides 2 cases of UX:
 *
 * Case 1: without Google reCAPTCHA
 * - A user clicks on the DonationStripeButton
 * - Payment sheet popup will be opened. It can be Google Pay, Apple Pay or popup provided by a browser
 * - User selected and confirmed payment details, after that new order will be created on the backend
 * - Draft payment is created on the backend. User may see 3d secure poppup from a bank
 * - Payment completed and user redirected to a Thank you page
 *
 * Case 2: with Google reCAPTCHA
 * - A user clicks on the DonationStripeButton
 * - Payment sheet popup will be opened. It can be Google Pay, Apple Pay or popup provided by a browser
 * - User selected and confirmed payment details
 * - User may see reCAPTCHA challenge. When the challenge is passed new order will be created on the backend
 * - Draft payment is created on the backend. User may see 3d secure poppup from a bank
 * - Payment completed and user redirected to a Thank you page
 */

const DonationStripeButton = ({
  paymentMethodId,
  showButton,
  amount = 0,
  dispatch = () => {},
  onSubmitDonation = () => {},
  donationType,
  orderId = null,
}) => {
  return (
    <StripePaymentButtonElement
      amount={amount}
      onSubmitDonation={onSubmitDonation}
      orderId={orderId}
      donationType={donationType}
      paymentMethodId={paymentMethodId}
      showButton={showButton}
      onUnexpectedError={() => {
        dispatch(
          donationFormSubmitError({
            errorMessage:
              "Sorry, we couldn't complete your donation. Please review your details and try again or select an alternative payment method.",
          }),
        );
        dispatch(donationFormUnLock());
      }}
      onSuccessPaymentCompleted={(id) => {
        dispatch(donationFormPaymentSuccess(id));
        dispatch(donationFormUnLock());
      }}
      beforePaymentMethod={() => dispatch(donationFormLock())}
      onStripeButtonClick={(paymentMethodId, continuePayment) => {
        continuePayment();
      }}
      onCancelPayment={() => dispatch(donationFormUnLock())}
      onConfirmCardPaymentError={() => {
        dispatch(
          donationFormSubmitError({
            errorMessage:
              "Sorry, we couldn't complete your donation. Please review your card details or try a different payment method.",
            subline: unexpectedErrorMessage(),
          }),
        );
        dispatch(donationFormUnLock());
      }}
      onStripeButtonAvailabilityCheck={(result) =>
        // It's done BEFORE payment step and result is shared in Redux store so
        // other components can check Stripe button availability at any time.
        dispatch(donationFormStripeButtonAvailability(result))
      }
      onConfirmCardPaymentActionError={() => {
        dispatch(
          donationFormSubmitError({
            errorMessage:
              "Sorry, we couldn't complete the donation. Please try a different payment method.",
            subline: unexpectedErrorMessage(),
          }),
        );
        dispatch(donationFormUnLock());
      }}
    />
  );
};

DonationStripeButton.propTypes = {
  paymentMethodId: PropTypes.string.isRequired,
  showButton: PropTypes.bool.isRequired,
  amount: PropTypes.number,
  onSubmitDonation: PropTypes.func,
  donationType: PropTypes.oneOf([SD, RG]).isRequired,
  dispatch: PropTypes.func,
  orderId: PropTypes.number,
};

const mapStateToProps = ({
  donationForm: {
    orderID,
    form: { values },
  },
}) => ({
  amount: values.fundraisingCostsApplied
    ? values.totalAmount
    : values.amount
      ? parseInt(values.amount, 10)
      : 0,
  orderId: orderID,
});

export default connect(mapStateToProps)(DonationStripeButton);
